import {
	BCAnchor,
	BCLayoutCreator,
	BCScrollbarRef,
	IAnchorLinkItemProps,
	IAnchorsItem
} from '@bimser/components';
import * as LayoutCreatorEntities from '@bimser/components/dist/BCLayoutCreator/common/entities';
import { FormItemTypes, IDictionary, MLHelper } from '@bimser/core';
import React from 'react';
import Styles from '../../assets/panelBaseStyles.scss';
import ContainerControl from '../../base/ContainerControl';
import { IScrollProperties } from './entities/IScrollProperties';
import { IScrollViewItem } from './entities/IScrollViewItem';

import classNames from 'classnames/bind';
const cx = classNames.bind(Styles);

export default class ScrollView extends ContainerControl {
	__selectedItem: string;
	__scrollViewItems: IScrollViewItem[];

	__layoutCreatorRefs = React.createRef<{
		container: HTMLDivElement;
		scrollView: BCScrollbarRef;
	}>();

	constructor() {
		super();
		this.controlType = FormItemTypes.EControlTypes.ScrollView;
		this.onClick = this.onClick.bind(this);
		this.onAnchorChange = this.onAnchorChange.bind(this);
		this.getCurrentAnchor = this.getCurrentAnchor.bind(this);
		this.getContainer = this.getContainer.bind(this);
		this.handleScroll = this.handleScroll.bind(this);
		this.updateUIForSelectedItem = this.updateUIForSelectedItem.bind(this);
		this.getCurrentAnchor = this.getCurrentAnchor.bind(this);

		this.caption.visible = false;
		this.style.height = 300;
		this.__scrollViewItems = [];
	}

	__loadProperties(data: IScrollProperties) {
		super.__loadProperties(data);

		this.__proxyChanged.__scrollViewItems = data.scrollViewItems || [];
		this.__proxyChanged.__selectedItem = data.selectedItem;
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			__scrollViewItems: this.__scrollViewItems,
			__selectedItem: this.__selectedItem
		};
	}

	updateUIForSelectedItem(__selectedItem: string) {
		document.querySelectorAll('.ant-anchor-link').forEach(link => {
			link.classList.remove('ant-anchor-link-active');
		});
		document.querySelectorAll('.ant-anchor-link-title').forEach(linkTitle => {
			linkTitle.classList.remove('ant-anchor-link-title-active');
		});

		const newActiveLinkTitle = document.querySelector(
			`.ant-anchor-link-title[href="${__selectedItem}"]`
		);
		if (newActiveLinkTitle) {
			newActiveLinkTitle.classList.add('ant-anchor-link-title-active');
			const newActiveLink = newActiveLinkTitle.closest('.ant-anchor-link');
			if (newActiveLink) {
				newActiveLink.classList.add('ant-anchor-link-active');
			}
		}
	}

	private handleScroll() {
		let activeItem = null;
		let closestTop = Infinity;
		const offset = -100;

		for (const item of this.__scrollViewItems) {
			const element = document.getElementById(item.href);
			if (element) {
				const rect = element.getBoundingClientRect();
				if (rect.top >= -offset && rect.top < closestTop) {
					closestTop = rect.top;
					activeItem = '#' + item.href;
				}
			}
		}

		if (activeItem && activeItem !== this.__selectedItem) {
			this.__proxyChanging.__selectedItem = activeItem;
			this.updateUIForSelectedItem(activeItem);
		}
	}

	private onClick(e: React.MouseEvent, link: IAnchorsItem) {
		e.preventDefault();
		const element = document.getElementById(link.href.substring(1));
		if (element) {
			element.scrollIntoView({ behavior: 'smooth', block: 'start' });
			this.__proxyChanging.__selectedItem = link.href;
		}
	}

	getContainer(): HTMLElement {
		if (!this.__layoutCreatorRefs.current) {
			return document.body;
		}
		const scrollViewRef = this.__layoutCreatorRefs.current.scrollView as any;
		const scrollRefContainer =
			scrollViewRef?.container?.firstChild?.firstChild || document.body;
		return scrollRefContainer;
	}

	onAnchorChange(currentActiveLink: string) {
		if (currentActiveLink && this.__selectedItem !== currentActiveLink) {
			const element = this.__form
				.__getShadowRootContainer()
				.querySelector(`#${currentActiveLink.substring(1)}`);
			if (element) {
				element.scrollIntoView({ behavior: 'smooth', block: 'start' });
				this.__proxyChanging.__selectedItem = currentActiveLink;
			}
		}
	}

	private getAnchorItems(items: IScrollViewItem[] = []): IAnchorLinkItemProps[] {
		return items.map(i => ({
			key: i.href,
			href: `#${i.href}`,
			title: MLHelper.getMLText(i.title),
			children: this.getAnchorItems(i.children)
		}));
	}

	getCurrentAnchor(activeLink: string) {
		if (activeLink) {
			return activeLink;
		}

		if (this.__scrollViewItems.length) {
			return `#${this.__scrollViewItems[0].href}`;
		}
	}

	__parentItem: LayoutCreatorEntities.LayoutStructureItem;
	__getLayoutParentItem(): LayoutCreatorEntities.LayoutStructureItem {
		const newParentItem = LayoutCreatorEntities.LayoutStructureItemFactory({
			id: this.name,
			type: this.controlType,
			items: this.__childrenItem.result
		});

		if (this.__parentItem?.equals(newParentItem)) {
			return this.__parentItem;
		}

		return newParentItem;
	}

	__render() {
		const panelContainerClassNames = cx({
			panelContainer: true,
			scrollView: true
		});

		const panelContentContainerClassNames = cx({
			panelContentContainer: true,
			collapsed: false
		});

		return super.__render(
			<div className={panelContainerClassNames}>
				<BCAnchor
					affix={false}
					showInkInFixed={false}
					getContainer={this.getContainer}
					items={this.getAnchorItems(this.__scrollViewItems)}
					onClick={this.onClick}
					onChange={this.onAnchorChange}
					disabled={this.__isDisabled()}
					getCurrentAnchor={this.getCurrentAnchor}
				/>
				<div className={panelContentContainerClassNames}>
					<BCLayoutCreator.Viewer
						containerClassName={`CSP_form_${this.__form.projectName}_${this.__form.formName}`}
						view={this.__childrenItem}
						controlRenderManager={this.__form.__controlRenderManager}
						isControlVisible={this.__form.__isControlVisible}
						parentItem={this.__getLayoutParentItem()}
						isSubLayout
						containerStyles={{ ...this.style, minHeight: '150px' }}
						windowSize={{ width: this.__form.windowSize?.width || 1000 }}
						hasScrollbar
						ref={this.__layoutCreatorRefs}
						onScroll={this.handleScroll}
					/>
				</div>
			</div>
		);
	}
}
